import * as React from 'react';
import classNames from 'classnames';

import {
  ISearchBoxProps,
  ISearchBoxImperativeActions,
} from '../SearchBox.types';
import { ITextInputImperativeActions } from '../../TextInput/TextInput.types';
import { keyCodes } from '../../../core/commons/a11y';
import { Suggestions } from './suggestionsComponent/Suggestions';
import * as translations from './i18n.constants';
import { SearchBoxLayout } from './searchBoxLayout/SearchBoxLayout';

import style from './style/SearchBox.scss';

const SearchBox: React.ForwardRefRenderFunction<
  ISearchBoxImperativeActions,
  ISearchBoxProps
> = (props, ref) => {
  const {
    id,
    onSubmit = noop,
    placeholder = '',
    suggestions,
    translate,
    value = '',
    onKeyPress = noop,
    onFocus = noop,
    onBlur = noop,
    onChange = noop,
    onClick = noop,
    onDblClick = noop,
    onClear = noop,
    isDisabled = false,
    isSuggestionsOpen = false,
    suggestionsEnabled = false,
    onSuggestionsFooterClick = noop,
    onSuggestionSelect = noop,
    onSuggestionGroupSelect = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
  } = props;

  React.useImperativeHandle(ref, () => {
    return {
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
    };
  });

  const inputRef = React.useRef<ITextInputImperativeActions>(null);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (
    e: React.FormEvent,
  ) => {
    e.preventDefault();
    inputRef.current?.blur();
    onSubmit(e);
  };

  const clearButtonLabel = translate!(
    translations.NAMESPACE,
    translations.CLEAR_BUTTON_LABEL_KEY,
    translations.CLEAR_BUTTON_LABEL_DEFAULT,
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.keyCode === keyCodes.escape) {
      onClear();
    }
  };

  const shouldShowSuggestions = Boolean(
    isSuggestionsOpen && suggestionsEnabled && suggestions,
  );

  return (
    <div
      id={id}
      className={classNames(style.root, shouldShowSuggestions && style.focused)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Suggestions
        onSuggestionGroupSelect={onSuggestionGroupSelect}
        onSuggestionSelect={onSuggestionSelect}
        onSuggestionsFooterClick={onSuggestionsFooterClick}
        query={value}
        shouldShowSuggestions={shouldShowSuggestions}
        suggestions={suggestions}
        translate={translate!}
      >
        <form
          className={classNames(
            style.container,
            shouldShowSuggestions && style.expanded,
          )}
          data-testid="search-box-form"
          onSubmit={handleSubmit}
          role="search"
          onClick={() => inputRef.current?.focus()}
          onKeyDown={handleKeyDown}
        >
          <SearchBoxLayout
            clearButtonLabel={clearButtonLabel}
            disabled={isDisabled}
            id={id}
            inputRef={inputRef}
            onBlur={onBlur}
            onClear={onClear}
            onClick={onClick}
            onDblClick={onDblClick}
            onFocus={onFocus}
            onInput={onChange}
            onKeyPress={onKeyPress}
            placeholder={placeholder}
            value={value}
          />
        </form>
      </Suggestions>
    </div>
  );
};

export default React.forwardRef(SearchBox);

function noop() {}
